import { useCallback } from 'react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest } from './msalConfig';

export type TokenGetter = () => Promise<string>;

/**
 * Returns a getter that acquires the API scope silently and falls back to an interactive redirect
 * when the refresh token is gone or consent is missing. The redirect never resolves (the page
 * navigates away), so callers should treat a thrown error as "request abandoned".
 */
export function useAccessToken(): TokenGetter {
  const { instance, inProgress } = useMsal();

  return useCallback(async () => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) throw new Error('No signed-in account');
    try {
      const result = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError && inProgress === InteractionStatus.None) {
        await instance.acquireTokenRedirect({ ...apiTokenRequest, account });
        // acquireTokenRedirect navigates away; this promise is never observed in practice.
        return new Promise<string>(() => undefined);
      }
      throw err;
    }
  }, [instance, inProgress]);
}
